import {useMap} from '@vis.gl/react-google-maps';
import {useEffect, useMemo, useRef, useState} from 'react';

import {GoogleMapsOverlay} from '@deck.gl/google-maps/typed';

import type {LayersList} from '@deck.gl/core/typed';
import { useLayerVisibilityStore, useEventInfoStore, useVotingPlaceStore, useAppStateStore } from "../../store/layerStore";
import { MVTLayer } from 'deck.gl/typed';
import axios from 'axios';
import { IEventInfo } from '../../model/Event';
import { VotingPlace } from '../../model';
import { AppScopes, AppStates, UserStates, VPTypeCodes } from '../../util';
import { VP_WAIT_TIME_URL } from '../../api';
import { shouldDisplayWaitTimes } from '../../util';

export type DeckglOverlayProps = {layers?: LayersList};

// const VP_URL = 'https://ebcwtvgeoserver.canadacentral.cloudapp.azure.com/geoserver/wtv/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=wtv%3AEBC_VOTING_PLACES&maxFeatures=5000&cql_filter=ADV_SCORE%20%3E=%208%20AND%20ED_ABBR%20IN%20(%27VMP%27)&outputFormat=application%2Fjson';
export const GEOSERVER_ROOT_URL = 'https://ebcwtvgeoserver.canadacentral.cloudapp.azure.com/geoserver/';
// export const GEOSERVER_ROOT_URL = 'https://ebcwtvgeoserver-01.canadacentral.cloudapp.azure.com:8443/geoserver/';
// export const GEOSERVER_ROOT_URL_ = 'https://ebcwtvgeoserver-01.canadacentral.cloudapp.azure.com:8443/geoserver/';

/**
 * A very simple implementation of a component that renders a list of deck.gl layers
 * via the GoogleMapsOverlay into the <Map> component containing it.
 */
// export const DeckGlOverlay = ({layers}: DeckglOverlayProps) => {
export const Bs11DeckGlOverlay = () => {
    const { selected, edInfo } = useLayerVisibilityStore();
    const { eventInfo } = useEventInfoStore();
    const { userState, userElectoralDistrict } = useAppStateStore();
    // The GoogleMapsOverlay can persist throughout the lifetime of the DeckGlOverlay
    const overlay = useMemo(() => bs11GoogleMapsOverlay(),[]);
  
    // const layerClick = (info:PickingInfo, event:any) => {
    //     setShowInfoWindow(false);
    //     setSelected(info);
    //     const o = bs11EdInfo(info.object.properties.edabbr);
    //     // const bbox: turf.helpers.BBox = turf.bbox(info.object.geometry);
    //     const b = bs11EdBounds(info.object.properties.edabbr);
    
    //     setHighlightInfo({...o, bs:'11'});

    //     setMarkerLat(event.srcEvent.latLng.lat());
    //     setMarkerLng(event.srcEvent.latLng.lng());
    //     setShowInfoWindow(true);
    //     map?.fitBounds(b);
    // }  
    //https://ebcwtvgeoserver.canadacentral.cloudapp.azure.com/geoserver
    const lyr = bs11MVTLayer(
        'bs11', 
        `${GEOSERVER_ROOT_URL}gwc/service/tms/1.0.0/wtv:EBC_ELECTORAL_DISTS_BS11@EPSG:900913@pbf/{z}/{x}/{-y}.pbf`, 
        true, 
        selected ? selected.object.properties.edabbr : "",
        edInfo,
        // setSelected,
        () => {},
        userState,
        userElectoralDistrict,
        eventInfo,
    );
    const layers = [lyr];

    const map = useMap();
    useEffect(() => {
        overlay.setMap(map);
    }, [map]);

    // Whenever the rendered data changes, the layers will be updated
    useEffect(() => {
        const lyrUpdates = bs11MVTLayer(
            'bs11', 
            `${GEOSERVER_ROOT_URL}gwc/service/tms/1.0.0/wtv:EBC_ELECTORAL_DISTS_BS11@EPSG:900913@pbf/{z}/{x}/{-y}.pbf`, 
            true, 
            edInfo ? edInfo.edAbbr : "",
            edInfo,
            () => {}, //layerClick,
            userState,
            userElectoralDistrict,
            eventInfo,
        );
        overlay.setProps({layers: [lyrUpdates],});
    }, [layers, userState, userElectoralDistrict]); //was [layers]

    // No dom rendered by this component
    return null;
};

export const VpLayerCqlFilter = (appState: string, addressId?: number, edAbbrevs?: string[]) => {
    // We want to show "deo", "general" and "general and advance" (all with score of 64) and also different advance VPs over a specific value based on the app state
    let cqlFilter = "ADV_SCORE >= ";
    switch (appState) {
       case AppStates.PRE_ADVANCE:
       case AppStates.ADVANCE_1:
          cqlFilter += '1';
          break;
       case AppStates.ADVANCE_2:
          cqlFilter += '2';
          break;
       case AppStates.ADVANCE_3:
          cqlFilter += '4';
          break;
       case AppStates.ADVANCE_4:
          cqlFilter += '8';
          break;
       case AppStates.ADVANCE_5:
          cqlFilter += '16';
          break;
       case AppStates.ADVANCE_6:
          cqlFilter += '32';
          break;
       case AppStates.ADVANCE_7:
          cqlFilter += '64';
          break;
       case AppStates.ADVANCE_8:
          cqlFilter += '128';
          break;
       case AppStates.ADVANCE_9:
          cqlFilter += '256';
          break;
       default:
          cqlFilter += '512';
          break;
    }
    if (addressId) {
       // Add the address standard ID to the clause
       cqlFilter += ` AND ADDR_ST_ID <> ${addressId}`
    }
    if (edAbbrevs && edAbbrevs.length > 0) {       
         const quotedEdAbbrevs = edAbbrevs
             .map((ed) => `'${ed.replace(/'/g, "''")}'`)
             .join(',');
         cqlFilter += ` AND ED_ABBR IN (${quotedEdAbbrevs})`;
    }
    return encodeURI(cqlFilter);
}

export const VotingPlacesLayer = () => {
    const { setActiveVotingPlace } = useVotingPlaceStore();
    const { voterElectoralDistrictInfo, userState } = useAppStateStore();
    const { eventInfo } = useEventInfoStore();
    const isInitialMount = useRef(true);
    const edAbbrevsForScope = eventInfo?.scope === AppScopes.BYELECTION ? eventInfo.electoralDistricts : undefined;
    const geoJsonUrl = GEOSERVER_ROOT_URL + 'wtv/ows?'+ 
    'service=WFS' + 
    '&version=1.0.0' +
    '&request=GetFeature' +
    '&typeName=wtv%3AEBC_VOTING_PLACES' +
    '&maxFeatures=5000' +
    `&cql_filter=${VpLayerCqlFilter(eventInfo?.state||'', undefined, edAbbrevsForScope)}` +
    '&outputFormat=application%2Fjson';
    
    const map = useMap();
    useEffect(() => {
        // console.log("*** VotingPlacesLayer:",isInitialMount.current, map?.data);
        if (isInitialMount.current && eventInfo) {
            isInitialMount.current = false;
            map?.data.loadGeoJson(geoJsonUrl, {}, 
                (features) => {
                    // console.log("*** VotingPlacesLayer: data loaded",features);
                    map?.data.setStyle((feature) => {
                        var color: any;
                        const title: any = feature.getProperty('BLDNG_NAME');
                        // if (lyrConfig.addressId && feature.getProperty('ADDR_ST_ID') && feature.getProperty('ADDR_ST_ID') === lyrConfig.addressId) {
                        if (voterElectoralDistrictInfo && voterElectoralDistrictInfo.assignedVotingPlace && feature.getProperty('ADDR_ST_ID') && feature.getProperty('ADDR_ST_ID') === voterElectoralDistrictInfo.assignedVotingPlace.id) {
                            color = feature.getProperty('ICON_3');
                        } else {
                            // color = eventInfo?.state === 'advance_1' || eventInfo?.state === 'pre_advance' ? feature.getProperty('ICON_1') : feature.getProperty('ICON_2'); //feature.getProperty(`${lyrConfig.colorField}`);
                            color = eventInfo?.state === AppStates.POST_ADVANCE || eventInfo?.state === AppStates.GENERAL ? feature.getProperty('ICON_2') : feature.getProperty('ICON_1');
                        }
                        return /** @type {!google.maps.Data.StyleOptions} */ {
                            icon: {
                                path: 'M12 2c3.86 0 7 3.14 7 7 0 5.25-7 13-7 13S5 14.25 5 9c0-3.86 3.14-7 7-7zm-1.53 12L17 7.41 15.6 6l-5.13 5.18L8.4 9.09 7 10.5l3.47 3.5z',
                                // path: "M12,2C15.86,2 19,5.14 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9C5,5.14 8.14,2 12,2M10.47,14L17,7.41L15.6,6L10.47,11.18L8.4,9.09L7,10.5L10.47,14Z",
                                scale: 1.2,
                                anchor: new google.maps.Point(12,24),
                                strokeWeight: .8,
                                strokeColor: '#595959',
                                strokeOpacity: 1,
                                fillColor: color,
                                fillOpacity: .9,
                            },
                            title: title,
                            zIndex: 1,
                        };  
                    });
                    map?.data.addListener('click', (e: any) => {
                        let vp = new VotingPlace(e.feature.getProperty('ADDR_ST_ID'), e.feature.getProperty('BLDNG_NAME'), e.feature.getProperty('ADDRESS'), e.feature.getProperty('LOCALITY'), e.feature.getProperty('VP_TYPE_ID'),
                            e.feature.getProperty('VP_TYPE'), {lng: e.feature.getProperty('POINT_X'), lat: e.feature.getProperty('POINT_Y')}, e.feature.getProperty('ED_ABBR'), e.feature.getProperty('ED_NAME'), e.feature.getProperty('MST_IND') === 'Y',
                            e.feature.getProperty('WHLCHR_ACC') === 'Y', e.feature.getProperty('OPEN_ADV_1'), e.feature.getProperty('OPEN_ADV_2'), e.feature.getProperty('OPEN_ADV_3'), e.feature.getProperty('OPEN_ADV_4'), e.feature.getProperty('OPEN_ADV_5'),
                            e.feature.getProperty('OPEN_ADV_6'), e.feature.getProperty('OPEN_ADV_7'), e.feature.getProperty('OPEN_ADV_8'), e.feature.getProperty('OPEN_ADV_9'), e.feature.getProperty('ADV_SCORE'), e.feature.getProperty('ICON_1'), 
                            e.feature.getProperty('ICON_2'), e.feature.getProperty('ICON_3'), 0, 0, new Date(), false,''
                        );
                        if (shouldDisplayWaitTimes(eventInfo?.scope) && (vp.votingPlaceTypeCode === VPTypeCodes.ADVANCE || vp.votingPlaceTypeCode === VPTypeCodes.ADVANCE_FINAL)) {
                            axios.get(`${VP_WAIT_TIME_URL}${e.feature.getProperty('ADDR_ST_ID')}`,{}).catch((err:any) => {
                                console.log("Error: ", err);
                            }).then((r: any) => {
                                vp.waitTime = r.data;
                                setActiveVotingPlace(vp);
                            });
                        } else {
                            setActiveVotingPlace(vp);
                        }
                    });                    
                });

            // map?.data.addListener('click', (e: any) => {
            //     let vp = new VotingPlace(e.feature.getProperty('ADDR_ST_ID'), e.feature.getProperty('BLDNG_NAME'), e.feature.getProperty('ADDRESS'), e.feature.getProperty('LOCALITY'), e.feature.getProperty('VP_TYPE_ID'),
            //         e.feature.getProperty('VP_TYPE'), {lng: e.feature.getProperty('POINT_X'), lat: e.feature.getProperty('POINT_Y')}, e.feature.getProperty('ED_ABBR'), e.feature.getProperty('ED_NAME'), e.feature.getProperty('MST_IND') === 'Y',
            //         e.feature.getProperty('WHLCHR_ACC') === 'Y', e.feature.getProperty('OPEN_ADV_1'), e.feature.getProperty('OPEN_ADV_2'), e.feature.getProperty('OPEN_ADV_3'), e.feature.getProperty('OPEN_ADV_4'), e.feature.getProperty('OPEN_ADV_5'),
            //         e.feature.getProperty('OPEN_ADV_6'), e.feature.getProperty('OPEN_ADV_7'), e.feature.getProperty('OPEN_ADV_8'), e.feature.getProperty('OPEN_ADV_9'), e.feature.getProperty('ADV_SCORE'), e.feature.getProperty('ICON_1'), 
            //         e.feature.getProperty('ICON_2'), e.feature.getProperty('ICON_3'), 0, 0, new Date(), false,''
            //     );
            //     if (vp.votingPlaceTypeCode === VPTypeCodes.ADVANCE || vp.votingPlaceTypeCode === VPTypeCodes.ADVANCE_FINAL) {
            //         axios.get(`${VP_WAIT_TIME_URL}${e.feature.getProperty('ADDR_ST_ID')}`,{}).catch((err:any) => {
            //             console.log("Error: ", err);
            //         }).then((r: any) => {
            //             vp.waitTime = r.data;
            //             setActiveVotingPlace(vp);
            //         });
            //     } else {
            //         setActiveVotingPlace(vp);
            //     }
            // });
        }
    }, [map, eventInfo]);

    useEffect(() => {
        map?.data.setStyle((feature) => {
            var color: any;
            const title: any = feature.getProperty('BLDNG_NAME');
            // if (lyrConfig.addressId && feature.getProperty('ADDR_ST_ID') && feature.getProperty('ADDR_ST_ID') === lyrConfig.addressId) {
            
            if (voterElectoralDistrictInfo && userState === UserStates.ED && voterElectoralDistrictInfo.assignedVotingPlace !== null && feature.getProperty('ADDR_ST_ID') && feature.getProperty('ADDR_ST_ID') === voterElectoralDistrictInfo.assignedVotingPlace.id) {
                color = feature.getProperty('ICON_3');
            } else {
                // color = eventInfo?.state == 'advance_1' || eventInfo?.state == 'pre_advance' ? feature.getProperty('ICON_1') : feature.getProperty('ICON_2');
                color = eventInfo?.state === AppStates.POST_ADVANCE || eventInfo?.state === AppStates.GENERAL ? feature.getProperty('ICON_2') : feature.getProperty('ICON_1'); 
                //feature.getProperty(`${lyrConfig.colorField}`);
            }

            // A marker with a custom inline SVG.
            const pinSvgString = '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none"><rect width="56" height="56" rx="28" fill="#7837FF"></rect><path d="M46.0675 22.1319L44.0601 22.7843" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M11.9402 33.2201L9.93262 33.8723" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M27.9999 47.0046V44.8933" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M27.9999 9V11.1113" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M39.1583 43.3597L37.9186 41.6532" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M16.8419 12.6442L18.0816 14.3506" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M9.93262 22.1319L11.9402 22.7843" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M46.0676 33.8724L44.0601 33.2201" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M39.1583 12.6442L37.9186 14.3506" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M16.8419 43.3597L18.0816 41.6532" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M28 39L26.8725 37.9904C24.9292 36.226 23.325 34.7026 22.06 33.4202C20.795 32.1378 19.7867 30.9918 19.035 29.9823C18.2833 28.9727 17.7562 28.0587 17.4537 27.2401C17.1512 26.4216 17 25.5939 17 24.7572C17 23.1201 17.5546 21.7513 18.6638 20.6508C19.7729 19.5502 21.1433 19 22.775 19C23.82 19 24.7871 19.2456 25.6762 19.7367C26.5654 20.2278 27.34 20.9372 28 21.8649C28.77 20.8827 29.5858 20.1596 30.4475 19.6958C31.3092 19.2319 32.235 19 33.225 19C34.8567 19 36.2271 19.5502 37.3362 20.6508C38.4454 21.7513 39 23.1201 39 24.7572C39 25.5939 38.8488 26.4216 38.5463 27.2401C38.2438 28.0587 37.7167 28.9727 36.965 29.9823C36.2133 30.9918 35.205 32.1378 33.94 33.4202C32.675 34.7026 31.0708 36.226 29.1275 37.9904L28 39Z" fill="#FF7878"></path></svg>';
            const parser = new DOMParser();
            const pinSvg = parser.parseFromString(pinSvgString, 'image/svg+xml').documentElement;


            return /** @type {!google.maps.Data.StyleOptions} */ {
                icon: {
                    path: 'M12 2c3.86 0 7 3.14 7 7 0 5.25-7 13-7 13S5 14.25 5 9c0-3.86 3.14-7 7-7zm-1.53 12L17 7.41 15.6 6l-5.13 5.18L8.4 9.09 7 10.5l3.47 3.5z',
                    // path: "M12,2C15.86,2 19,5.14 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9C5,5.14 8.14,2 12,2M10.47,14L17,7.41L15.6,6L10.47,11.18L8.4,9.09L7,10.5L10.47,14Z",
                    // url: './images/map-marker-check.svg',
                    scale: 1.2,
                    anchor: new google.maps.Point(12,24),
                    strokeWeight: .8,
                    strokeColor: '#595959', //'white',
                    strokeOpacity: 1,
                    fillColor: color,
                    fillOpacity: .9,
                },
                title: title,
            };  
        });  
    },[voterElectoralDistrictInfo, userState]);

    return null;
}



export const VotingPlacesMarkerLayer = () => {
    const { setActiveVotingPlace } = useVotingPlaceStore();
    const { voterElectoralDistrictInfo, userState } = useAppStateStore();
    const { eventInfo } = useEventInfoStore();
    const isInitialMount = useRef(true);
    const edAbbrevsForScope = eventInfo?.scope === AppScopes.BYELECTION ? eventInfo.electoralDistricts : undefined;
    const geoJsonUrl = GEOSERVER_ROOT_URL + 'wtv/ows?'+ 
    'service=WFS' + 
    '&version=1.0.0' +
    '&request=GetFeature' +
    '&typeName=wtv%3AEBC_VOTING_PLACES' +
    '&maxFeatures=5000' +
    `&cql_filter=${VpLayerCqlFilter(eventInfo?.state||'', undefined, edAbbrevsForScope)}` +
    '&outputFormat=application%2Fjson';
    
    const map = useMap();
    useEffect(() => {
        if (isInitialMount.current && eventInfo) {
            isInitialMount.current = false;
            const closedVpIds = eventInfo.votingPlaceClosures.map(c => c.addressStandardId);
            map?.data.setStyle( function(feature: google.maps.Data.Feature){
                let color: string;
                let title: any = feature.getProperty('BLDNG_NAME');
                // if (lyrConfig.addressId && feature.getProperty('ADDR_ST_ID') && feature.getProperty('ADDR_ST_ID') === lyrConfig.addressId) {
                const addrId: number = typeof feature.getProperty('ADDR_ST_ID') === 'number' ? feature.getProperty('ADDR_ST_ID') as number : 0;                
                if (closedVpIds.includes(addrId)) {
                    const closureNote: string = eventInfo.votingPlaceClosures.filter(c => c.addressStandardId === addrId)[0].closureNote;
                    color = 'closure';
                    title = `${title} * ${closureNote} *`;
                } else if (voterElectoralDistrictInfo && userState === UserStates.ED && voterElectoralDistrictInfo.assignedVotingPlace !== null && addrId === voterElectoralDistrictInfo.assignedVotingPlace.id) {
                    color = `${feature.getProperty('ICON_3')}`.replace('#','');
                } else {
                    // color = eventInfo?.state == 'advance_1' || eventInfo?.state == 'pre_advance' ? feature.getProperty('ICON_1') : feature.getProperty('ICON_2');
                    color = eventInfo?.state === AppStates.POST_ADVANCE || eventInfo?.state === AppStates.GENERAL ? `${feature.getProperty('ICON_2')}`.replace('#','') : `${feature.getProperty('ICON_1')}`.replace('#',''); 
                }
                return {
                    title: title,
                    icon:`./img/${color}.png`
                };
            });

            map?.data.loadGeoJson(geoJsonUrl, {}, 
                (features) => {
                    // console.log(`Loaded GeoJSON ${features.length}`)
                    features.forEach(f => {
                        const addrId: number = typeof f.getProperty('ADDR_ST_ID') === 'number' ? f.getProperty('ADDR_ST_ID') as number : 0;
                        const closure = eventInfo.votingPlaceClosures.filter(c => c.addressStandardId === addrId);
                        if (closure.length === 1) {
                            const closureNote: string = closure[0].closureNote;
                            f.setProperty("CLOSURE_IND",true);
                            f.setProperty("CLOSURE_NOTE",closureNote);
                        } else {
                            f.setProperty("CLOSURE_IND",false);
                            f.setProperty("CLOSURE_NOTE",null);
                        }
                        return f;
                    });
                                    
                });

            map?.data.addListener('click', (e: any) => {
                console.log(e.feature);
                let vp = new VotingPlace(e.feature.getProperty('ADDR_ST_ID'), e.feature.getProperty('BLDNG_NAME'), e.feature.getProperty('ADDRESS'), e.feature.getProperty('LOCALITY'), e.feature.getProperty('VP_TYPE_ID'),
                    e.feature.getProperty('VP_TYPE'), {lng: e.feature.getProperty('POINT_X'), lat: e.feature.getProperty('POINT_Y')}, e.feature.getProperty('ED_ABBR'), e.feature.getProperty('ED_NAME'), e.feature.getProperty('MST_IND') === 'Y',
                    e.feature.getProperty('WHLCHR_ACC') === 'Y', e.feature.getProperty('OPEN_ADV_1'), e.feature.getProperty('OPEN_ADV_2'), e.feature.getProperty('OPEN_ADV_3'), e.feature.getProperty('OPEN_ADV_4'), e.feature.getProperty('OPEN_ADV_5'),
                    e.feature.getProperty('OPEN_ADV_6'), e.feature.getProperty('OPEN_ADV_7'), e.feature.getProperty('OPEN_ADV_8'), e.feature.getProperty('OPEN_ADV_9'), e.feature.getProperty('ADV_SCORE'), e.feature.getProperty('ICON_1'), 
                    e.feature.getProperty('ICON_2'), e.feature.getProperty('ICON_3'), 0, 0, new Date(), e.feature.getProperty('CLOSURE_IND'),e.feature.getProperty('CLOSURE_NOTE')
                );

                if (shouldDisplayWaitTimes(eventInfo?.scope) && (vp.votingPlaceTypeCode === VPTypeCodes.ADVANCE || vp.votingPlaceTypeCode === VPTypeCodes.ADVANCE_FINAL)) {
                    axios.get(`${VP_WAIT_TIME_URL}${e.feature.getProperty('ADDR_ST_ID')}`,{}).catch((err:any) => {
                        console.log("Error: ", err);
                    }).then((r: any) => {
                        vp.waitTime = r.data;
                        setActiveVotingPlace(vp);
                    });
                } else {
                    setActiveVotingPlace(vp);
                }
            }); 
        }
    }, [map, eventInfo]);

    useEffect(() => {
        if (eventInfo) {
            map?.data.setStyle( function(feature: google.maps.Data.Feature){
                let color: string;
                let title: any = feature.getProperty('BLDNG_NAME');
                // if (lyrConfig.addressId && feature.getProperty('ADDR_ST_ID') && feature.getProperty('ADDR_ST_ID') === lyrConfig.addressId) {
                
                const addrId: number = typeof feature.getProperty('ADDR_ST_ID') === 'number' ? feature.getProperty('ADDR_ST_ID') as number : 0;
                const closedVpIds: number[] = eventInfo.votingPlaceClosures.map(c => c.addressStandardId);
                if (closedVpIds.includes(addrId)) {
                    const closureNote: string = eventInfo.votingPlaceClosures.filter(c => c.addressStandardId === addrId)[0].closureNote;
                    color = 'closure';
                    title = `${title} * ${closureNote} *`;
                } else if (voterElectoralDistrictInfo && userState === UserStates.ED && voterElectoralDistrictInfo.assignedVotingPlace !== null && feature.getProperty('ADDR_ST_ID') && feature.getProperty('ADDR_ST_ID') === voterElectoralDistrictInfo.assignedVotingPlace.id) {
                    color = `${feature.getProperty('ICON_3')}`.replace('#','');
                } else {
                    // color = eventInfo?.state == 'advance_1' || eventInfo?.state == 'pre_advance' ? feature.getProperty('ICON_1') : feature.getProperty('ICON_2');
                    // color = eventInfo?.state === AppStates.POST_ADVANCE || eventInfo?.state === AppStates.GENERAL ? feature.getProperty('ICON_2') : feature.getProperty('ICON_1'); 
                    color = eventInfo?.state === AppStates.POST_ADVANCE || eventInfo?.state === AppStates.GENERAL ? `${feature.getProperty('ICON_2')}`.replace('#','') : `${feature.getProperty('ICON_1')}`.replace('#','');
                    //feature.getProperty(`${lyrConfig.colorField}`);
                }
                return {
                    title: title,
                    icon:`/img/${color}.png`
                };
            }); 
        }
    },[voterElectoralDistrictInfo, userState]);

    return null;
}

// const VpDeckGlOverlay = () => {
//     const { bs11, bs10, toggle, toggleBs11, toggleBs10, bs10Overlay, positionOverlay, selected, setSelected, searchPlace, setSearchPlace, ed, edInfo, markerLat, markerLng, setMarkerLat, setMarkerLng, setShowInfoWindow, highlightInfo, setHighlightInfo } = useLayerVisibilityStore();
//     // The GoogleMapsOverlay can persist throughout the lifetime of the DeckGlOverlay
    
//     const fetchVPData = React.useMemo(
//         () =>
//             async (
//                 request: { input: string },
//                 callback: (results?: any) => void,
//             ) => {
//                 const response = await axios.get(VP_URL).catch((err:any) => {
//                     console.log("Error: ", err);
//                 });
//                 if (response) {
//                     callback(response.data.value.map((f:any) =>  ({
//                         description: f.street_addr,
//                         result: f, 
//                         structured_formatting: {
//                             main_text: f.street_addr,
//                             secondary_text: `${f.ed_name} (${f.ed_abbr})`,
//                         }
//                     }) ));
//                         // setTvShows(response.data.value);
//                 }
//                 }
//             ,[]
//       );

//     const overlay = useMemo(() => vpGoogleMapsOverlay(),[]);
  
//     const layerClick = (info:PickingInfo, event:any) => {
//     } 
//     const lyr = vpIconLayer('vp','https://ebcwtvgeoserver.canadacentral.cloudapp.azure.com/geoserver/wtv/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=wtv%3AEBC_VOTING_PLACES&maxFeatures=5000&cql_filter=ADV_SCORE%20%3E=%208%20AND%20ED_ABBR%20IN%20(%27VMP%27)&outputFormat=application%2Fjson');
//     const layers = [lyr];

//     const map = useMap();
//     useEffect(() => {
//         overlay.setMap(map);
//         // fetchVPData({input: ''}, (results?: any) => {
//         //     console.log(results);      
//         // });
//     }, [map]);

//     // Whenever the rendered data changes, the layers will be updated
//     useEffect(() => {
//         const lyrUpdates = vpIconLayer('vp',''); 
//         overlay.setProps({layers: [lyrUpdates],});
//     }, [layers]); //was [layers]

//     // No dom rendered by this component
//     return null;
// };


const bs11GoogleMapsOverlay = () => {
    const o = new GoogleMapsOverlay({
        getTooltip: ({object}) => object && {
            // text:  `${object.properties.edname} (${object.properties.edabbr})`,
            html: `<div><b>${object.properties.edname} (${object.properties.edabbr})</b></div>`,
            // html: `<h3>${object.properties.edname} (${object.properties.edabbr})</h3>`,
            style: {
                display: 'inline',
                padding: '3px',
                backgroundColor: '#e8eaeda5', //'#e9e8dca5'
                borderRadius: '3px',
                // border: '1px solid #e9e8dcf1',
                fontSize: '0.75rem',
                // fontFamily: 'Bebas Neue',
                fontWeight: '400',
                color: '#db282e',
                width: 'auto',
                top: '-15px',
                left: '15px',
                whiteSpace: 'nowrap',
                boxShadow: '1px 1px 6px #565656a5',
            }
        },
        layers: [],
    });
    return o;
}

// const bs10GoogleMapsOverlay = (isOffsetTooltip: boolean) => {
//     const o = new GoogleMapsOverlay({
//         getTooltip: ({object}) => object && {
//             html: `<b>${object.properties.edname} (${object.properties.edabbr})</b>`,
//             style: {
//                 display: 'inline',
//                 padding: '3px',
//                 backgroundColor: '#e8eaeda5', //'#e9e8dca5',e8eaed
//                 borderRadius: '3px',
//                 // border: '1px solid #e9e8dcf1',
//                 fontSize: '0.75rem',
//                 fontWeight: '400',
//                 color: '#565656',
//                 width: 'auto',
//                 top: '15px', //23
//                 left: '15px',
//                 whiteSpace: 'nowrap',
//                 boxShadow: '1px 1px 6px #565656a5',
//             }
//         },
//         layers: [],
//     });
//     return o;
// }

// const vpGoogleMapsOverlay = () => {
//     const o = new GoogleMapsOverlay({
//         // getTooltip: ({object}) => object && {
//         //     html: `<b>${object.properties.edname} (${object.properties.edabbr})</b>`,
//         //     style: {
//         //         display: 'inline',
//         //         padding: '3px',
//         //         backgroundColor: '#e8eaeda5', //'#e9e8dca5',e8eaed
//         //         borderRadius: '3px',
//         //         // border: '1px solid #e9e8dcf1',
//         //         fontSize: '0.75rem',
//         //         fontWeight: '400',
//         //         color: '#565656',
//         //         width: 'auto',
//         //         top: '15px', //23
//         //         left: '15px',
//         //         whiteSpace: 'nowrap',
//         //         boxShadow: '1px 1px 6px #565656a5',
//         //     }
//         // },
//         layers: [],
//     });
//     return o;
// }

const bs11MVTLayer = (layerId: string, dataUrl: string, defaultVisibile: boolean, selected: string, edInfo: any, callback: any, userState: string, userElectoralDistrict: string | null, eventInfo?: IEventInfo) => {
  return new MVTLayer({
    id: layerId,
    data: dataUrl,
    minZoom: 2,
    maxZoom: 20,
    binary: true,
    stroked: true,    
    getFillColor: (f: any) => {
        const baseColor = [0, 0, 0, 0]; // This has no fill
        const lowlightColor = [56, 56, 56, 50];
        if (!f.properties) {
            return baseColor;
        }

        const byelectionTargetDistrict =
            eventInfo?.scope === AppScopes.BYELECTION && eventInfo.electoralDistricts.length > 0
                ? eventInfo.electoralDistricts[0]
                : null;
        const targetDistrict = userElectoralDistrict || byelectionTargetDistrict;
        const shouldLowlight =
            userState === UserStates.ED || eventInfo?.scope === AppScopes.BYELECTION;

        if (shouldLowlight && targetDistrict) {
            return targetDistrict === f.properties.edabbr ? baseColor : lowlightColor;
        }

        return baseColor;

        // if (f.properties && ((highlightInfo && highlightInfo.bs === "11") || edInfo)) {
        //     const isMatch = (edInfo && f.properties.edabbr == edInfo.edAbbr) || (highlightInfo && highlightInfo.bs === "11" && f.properties.edabbr == highlightInfo.edAbbr);
        //     return isMatch ? baseColor : lowlightColor;
        // } else {
        //     return baseColor;
        // }
    },
    getLineColor: [219, 40, 46], //[46, 125, 50], //[219, 40, 46],//[37, 255, 37],
    // getLineColor: (f: any) => { if(f.properties?.edabbr == selected) { return [37, 255, 37];} else return [219, 40, 46]; },
    autoHighlight: false,
    highlightColor: [219,40,46,25],
    lineWidthUnits: 'pixels',
    // getLineWidth: 2,
    getLineWidth: (f: any) => { 
        const isMatch = (edInfo && f.properties.edabbr === edInfo.edAbbr);
        return isMatch ? 2.3 : 2.3; 
    },
    lineWidthMinPixels: 0,
    pickable: true,
    onClick: callback,
    visible: defaultVisibile,
    onDataLoad: (data:any) => {
    },
    onError: (err) => {
    },
    // onHover: (data) => console.log(data),
    updateTriggers: {
         getFillColor: [edInfo, userState, userElectoralDistrict, eventInfo?.scope, eventInfo?.electoralDistricts?.join(',')],
       getLineWidth: [selected],
       getLineColor: [selected]
     }
  })
}